<template>
	<div
		ref="dropArea"
		:class="{
			'uploader--dragging' : isDragging,
		}"
		class="uploader"
		data-cy="uploader"
	>
		<transition name="fade">
			<div
				v-if="!loading"
				key="notLoading"
			>
				<div>
					<helio-icon
						token="cloud-upload"
						weight="light"
						class="uploader__icon"
					/>
					<p class="uploader__message">
						{{ $t('upload.uploader.drag-and-drop') }} <br> {{ $t('upload.uploader.or') }}
					</p>
					<helio-button
						:text="$t('upload.uploader.upload-file')"
						color="primary"
						class="uploader__button"
						@click.stop="handleUploadButton"
					/>
					<input
						ref="files"
						class="uploader__input-file"
						multiple
						type="file"
						@change="handleFileInput"
					>
				</div>
			</div>
			<div
				v-if="loading"
				key="whileLoading"
			>
				<helio-spinner
					color="blue"
				/>
			</div>
		</transition>
	</div>
</template>

<script>
import Button from '../../button/Button.vue';
import Icon from '../../icon/Icon.vue';
import Spinner from '../../spinner/Spinner.vue';
import Centios from '../../../utils/communication/Centios';

export default {
	components: {
		'helio-button': Button,
		'helio-icon': Icon,
		'helio-spinner': Spinner,
	},
	props: {
		maximumSize: {
			type: Number,
			required: true,
		},
		minimumWidth: {
			type: Number,
			required: true,
		},
		minimumHeight: {
			type: Number,
			required: true,
		},
		api: {
			type: String,
			required: true,
		},
		extensions: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			loading: false,
			isDragging: false,
			error: {},
			errorBuffer: {},
		};
	},
	computed: {
		cleanExtensions() {
			let cleanExtensions = '';
			let index = 1;

			this.extensions.forEach((extension) => {
				cleanExtensions += extension.replace('image/', '');

				if (index === this.extensions.length - 1) {
					cleanExtensions += ` ${this.$t('upload.uploader.or')} `;
				} else if (index !== this.extensions.length) {
					cleanExtensions += ', ';
				}

				index += 1;
			});

			return cleanExtensions;
		},
	},
	watch: {
		error() {
			this.$forceUpdate();
			this.$emit('hasError', this.error);
		},
	},
	beforeCreate() {
		this.$root.$off('add-media-in-gallery');
	},
	created() {
		this.$root.$on('add-media-in-gallery', () => {
			this.loading = false;
			this.isDragging = false;
		});
	},
	mounted() {
		const { dropArea } = this.$refs;
		dropArea.addEventListener('dragenter', this.handleDragEnter, false);
		dropArea.addEventListener('dragleave', this.handleDragLeave, false);
		dropArea.addEventListener('dragover', this.preventAndStopPropagation, false);
		dropArea.addEventListener('drop', this.handleDrop, false);
	},
	methods: {
		preventAndStopPropagation(event) {
			if (event && event !== undefined) {
				event.preventDefault();
				event.stopPropagation();
			}
		},
		handleDragEnter(event) {
			this.preventAndStopPropagation(event);
			this.isDragging = true;
		},
		handleDragLeave(event) {
			this.preventAndStopPropagation(event);
			this.isDragging = false;
		},
		handleFileInput(event) {
			this.preventAndStopPropagation(event);
			this.handleFiles(this.$refs.files.files);
		},
		handleDrop(event) {
			this.preventAndStopPropagation(event);
			const { files } = event.dataTransfer;
			this.handleFiles(files);
		},
		async handleFiles(files) {
			this.errorBuffer = {
				typeErrors: { errorMessage: this.$t('upload.uploader.errors.extension', [this.cleanExtensions]), fileNames: [] },
				sizeErrors: { errorMessage: this.$t('upload.uploader.errors.size', [this.maximumSize / 1000000]), fileNames: [] },
				widthErrors: { errorMessage: this.$t('upload.uploader.errors.width', [this.minimumWidth]), fileNames: [] },
				heightErrors: { errorMessage: this.$t('upload.uploader.errors.height', [this.minimumHeight]), fileNames: [] },
			};

			this.loading = true;
			const validFiles = [];
			const extensionErrorFiles = [];
			const sizeErrorFiles = [];
			let totalInvalidFiles = 0;

			([...files]).forEach((file) => {
				// check file for each file uploaded
				let fileIsValid = true;

				if (!this.extensions.includes(file.type)) {
					fileIsValid = false;
					// store a buffer of the errors in the function.
					extensionErrorFiles.push(file.name);
				}

				if (file.size > this.maximumSize) {
					fileIsValid = false;
					// store a buffer of the errors in the function.
					sizeErrorFiles.push(file.name);
				}

				if (fileIsValid) {
					validFiles.push(file);
				}
			});

			// once the looping has been completed,
			// if the buffer has errors, save it into the global error buffer.
			if (extensionErrorFiles.length > 0) {
				this.errorBuffer.hasErrors = true;
				this.errorBuffer.typeErrors.fileNames = extensionErrorFiles;
			}

			if (sizeErrorFiles.length > 0) {
				this.errorBuffer.hasErrors = true;
				this.errorBuffer.sizeErrors.fileNames = sizeErrorFiles;
			}

			// for each VALID file, call uploadFIle. which will check for other errors
			const validatedFiles = [];
			for (let i = 0; i < validFiles.length; i += 1) {
				// eslint-disable-next-line
				const file = await this.uploadFile(validFiles[i]);
				if (file !== false) {
					validatedFiles.push(file);
				}
			}

			if (validatedFiles.length > 0) {
				validatedFiles.forEach((file) => {
					this.$root.$emit('initiate-crop', {
						name: file.name,
						url: file.url,
						width: file.width,
						height: file.height,
						type: 'file',
					});
				});
			}

			this.$emit('hasError', this.errorBuffer);

			// if there are no valid files, then stop. set everything to false.
			if (validFiles.length === 0) {
				this.loading = false;
				this.isDragging = false;
			}

			totalInvalidFiles = this.errorBuffer.typeErrors.fileNames.length
			+ this.errorBuffer.sizeErrors.fileNames.length
			+ this.errorBuffer.widthErrors.fileNames.length
			+ this.errorBuffer.heightErrors.fileNames.length;

			this.errorBuffer.totalInvalidFiles = totalInvalidFiles;
			this.error = this.errorBuffer;
		},
		uploadFile(file) {
			const endpoint = `${this.api}/medias/create`;
			const formData = new FormData();
			formData.append('file', file);
			formData.append('extensions', this.extensions);
			formData.append('maximumSize', this.maximumSize);
			formData.append('minimumWidth', this.minimumWidth);
			formData.append('minimumHeight', this.minimumHeight);

			return new Promise((resolve) => {
				Centios.submit('post', endpoint, formData).then((response) => {
					if (Centios.isInvalid(response.data)) {
						if (response.data.message === 'width-minimum') {
							this.errorBuffer.hasErrors = true;
							this.errorBuffer.widthErrors.fileNames.push(file.name);
						}
						if (response.data.message === 'height-minimum') {
							this.errorBuffer.hasErrors = true;
							this.errorBuffer.heightErrors.fileNames.push(file.name);
						}
						this.loading = false;
						this.isDragging = false;
					} else if (response.data.code === 200) {
						resolve(response.data.file);
					}

					resolve(false);
				});
			});
		},
		handleUploadButton() {
			this.$refs.files.click();
		},
	},
};
</script>

<style lang="scss" scoped src="./uploader.scss" />
